// src/components/Studios.js
import React, { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  doc,
} from "firebase/firestore";
import { db } from "../firebase";
import PageHeader from "./ui/PageHeader";
import Button from "./ui/Button";
import Badge from "./ui/Badge";
import Modal from "./ui/Modal";
import { TextField, CheckboxField } from "./ui/Field";
import { TableSkeleton } from "./ui/Skeleton";

function Studios() {
  const [studios, setStudios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [currentStudio, setCurrentStudio] = useState({
    name: "",
    display_flg: true,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [showHidden, setShowHidden] = useState(false);

  useEffect(() => {
    fetchStudios();
  }, []);

  const fetchStudios = async () => {
    try {
      setLoading(true);
      const studiosCollection = collection(db, "studios");
      const studiosSnapshot = await getDocs(studiosCollection);
      const studiosList = studiosSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        display_flg:
          doc.data().display_flg !== undefined ? doc.data().display_flg : true,
      }));
      setStudios(studiosList);
    } catch (error) {
      console.error("Error fetching studios: ", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEdit = async () => {
    try {
      if (isEditing) {
        const studioRef = doc(db, "studios", currentStudio.id);
        await updateDoc(studioRef, {
          name: currentStudio.name,
          display_flg: currentStudio.display_flg,
        });
      } else {
        await addDoc(collection(db, "studios"), {
          name: currentStudio.name,
          display_flg: true,
        });
      }
      setShowModal(false);
      fetchStudios();
    } catch (error) {
      console.error("Error saving studio: ", error);
    }
  };

  const handleToggleDisplay = async (studio) => {
    try {
      const newDisplayState = !studio.display_flg;
      const message = newDisplayState
        ? "このスタジオを表示状態にしますか？"
        : "このスタジオを非表示にしますか？";

      if (window.confirm(message)) {
        const studioRef = doc(db, "studios", studio.id);
        await updateDoc(studioRef, { display_flg: newDisplayState });
        fetchStudios();
      }
    } catch (error) {
      console.error("Error updating studio display state: ", error);
    }
  };

  const openAddModal = () => {
    setCurrentStudio({ name: "", display_flg: true });
    setIsEditing(false);
    setShowModal(true);
  };

  const openEditModal = (studio) => {
    setCurrentStudio(studio);
    setIsEditing(true);
    setShowModal(true);
  };

  const filteredStudios = showHidden
    ? studios
    : studios.filter((studio) => studio.display_flg);

  return (
    <div>
      <PageHeader
        title="スタジオ管理"
        subtitle={loading ? undefined : `${filteredStudios.length}件を表示`}
        actions={
          <Button variant="primary" onClick={openAddModal}>
            <i className="ti ti-plus" aria-hidden="true" />
            新規追加
          </Button>
        }
      />

      <div style={{ marginBottom: 16 }}>
        <button
          type="button"
          className={`chip ${showHidden ? "chip--active" : ""}`}
          onClick={() => setShowHidden(!showHidden)}
        >
          <i
            className={`ti ${showHidden ? "ti-eye" : "ti-eye-off"}`}
            aria-hidden="true"
            style={{ marginRight: 6 }}
          />
          {showHidden ? "非表示も表示中" : "非表示スタジオも表示"}
        </button>
      </div>

      {loading ? (
        <TableSkeleton rows={5} columns={3} />
      ) : filteredStudios.length === 0 ? (
        <div className="card empty">
          <i className="ti ti-building empty__icon" aria-hidden="true" />
          <p style={{ margin: 0 }}>表示できるスタジオがありません</p>
        </div>
      ) : (
        <div className="card" style={{ overflow: "hidden" }}>
          <table className="table">
            <thead>
              <tr>
                <th>名前</th>
                <th style={{ width: 110 }}>状態</th>
                <th style={{ width: 120 }}>ID</th>
                <th style={{ width: 130 }} className="cell-actions">
                  操作
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredStudios.map((studio) => (
                <tr
                  key={studio.id}
                  className={!studio.display_flg ? "row-muted" : ""}
                >
                  <td style={{ fontWeight: 500 }}>{studio.name}</td>
                  <td>
                    {studio.display_flg ? (
                      <Badge tone="success" dot>
                        表示中
                      </Badge>
                    ) : (
                      <Badge tone="neutral" dot>
                        非表示
                      </Badge>
                    )}
                  </td>
                  <td>
                    <span className="mono">{studio.id.slice(0, 8)}</span>
                  </td>
                  <td className="cell-actions">
                    <button
                      type="button"
                      className="icon-btn"
                      aria-label="編集"
                      title="編集"
                      onClick={() => openEditModal(studio)}
                    >
                      <i className="ti ti-edit" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className="icon-btn"
                      aria-label={studio.display_flg ? "非表示にする" : "表示する"}
                      title={studio.display_flg ? "非表示にする" : "表示する"}
                      onClick={() => handleToggleDisplay(studio)}
                    >
                      <i
                        className={`ti ${
                          studio.display_flg ? "ti-eye-off" : "ti-eye"
                        }`}
                        aria-hidden="true"
                      />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        show={showModal}
        onClose={() => setShowModal(false)}
        title={isEditing ? "スタジオを編集" : "新規スタジオ追加"}
        footer={
          <>
            <Button onClick={() => setShowModal(false)}>キャンセル</Button>
            <Button variant="primary" onClick={handleAddEdit}>
              保存
            </Button>
          </>
        }
      >
        <TextField
          label="スタジオ名"
          value={currentStudio.name}
          onChange={(e) =>
            setCurrentStudio({ ...currentStudio, name: e.target.value })
          }
        />
        {isEditing && (
          <CheckboxField
            label="一覧に表示する"
            checked={currentStudio.display_flg}
            onChange={(e) =>
              setCurrentStudio({
                ...currentStudio,
                display_flg: e.target.checked,
              })
            }
          />
        )}
      </Modal>
    </div>
  );
}

export default Studios;
